import { useLayoutEffect, useRef, useState } from "react";
import Image from "./Image";
import { Radar, SlidersHorizontal, Megaphone, BarChart3, RefreshCw, Banknote, ShieldCheck, CheckCircle2, Undo2, } from "lucide-react";

/* rentSuccess "connected back office" map.

   Nine glass tiles sit in a staggered 2 / 3 / 4 pyramid under the rentSuccess mark. ONE trunk
   leaves the mark and forks, on two rounded shoulders, onto ONE bar that runs across the top.
   Each column peels off that bar with a rounded corner and drops onto its tile; the bar's two
   ends curve down into the outer columns (Collect, Refund). The trunk carries on through the bar
   to a second fork in the corridor under the top row, where Claim and Resolve swing out under
   Track and Manage, and ends in Analyze. Every strand is a full path from the mark, so strands
   share the trunk and the bar and simply leave it at their own column.

   Every tile has its own comet. All nine launch together; each rides at its own speed so that
   every tile in a tier is hit at the same instant (tier 0, then tier 1, then tier 2), and a tile
   lights only when its comet's head reaches it. The comet motion is driven by one animated
   custom property on the tree (--rs-t, see home.css) that each comet turns into its own
   stroke-dashoffset, so nine different speeds share one clock and never drift apart.

   All geometry lives in one unit space (1 unit = 0.1rem at the tree's design width: 340 x 180
   for a 34rem x 18rem canvas) so the SVG strands and the absolutely-positioned HTML tiles always
   line up. Tiles are 3.25rem squares = 32.5 units. */

const W = 340;
const H = 180;
const TILE = 32.5;
const HALF = TILE / 2;
const CENTER = W / 2;

/* Column centres (20 % .. 80 % of the width, 10 % apart) and the three tile rows. */
const COL = (pct) => (pct / 100) * W;
const TIER_Y = [56, 106, 156];
const TOP = (tier) => TIER_Y[tier] - HALF;
const BOTTOM = (tier) => TIER_Y[tier] + HALF;

/* The trunk drops TRUNK_Y units, then the two shoulders (radius FORK_R) land it on the bar, which
   therefore sits at TRUNK_Y + FORK_R. Every column leaves the bar on a DROP_R corner. The 40 % /
   60 % columns are the tight ones: FORK_R + DROP_R must fit in the 34 units between the trunk
   and those columns, and the bar plus a DROP_R corner must stay above the top row. */
const TRUNK_Y = 6;
const FORK_R = 14;
const DROP_R = 14;
const BAR_Y = TRUNK_Y + FORK_R;

/* Second fork: in the corridor between the top and middle rows, where the trunk splits three
   ways. Its arcs use the whole corridor height. */
const CORRIDOR_Y = (BOTTOM(0) + TOP(1)) / 2;
const JOG_R = Math.floor((TOP(1) - BOTTOM(0)) / 2) - 0.75;

const TILES = [
  // Top tier (2)
  { label: "Track", icon: Radar, col: 40, tier: 0 },
  { label: "Manage", icon: SlidersHorizontal, col: 60, tier: 0 },
  // Middle tier (3)
  { label: "Market", icon: Megaphone, col: 30, tier: 1 },
  { label: "Analyze", icon: BarChart3, col: 50, tier: 1 },
  { label: "Sync", icon: RefreshCw, col: 70, tier: 1 },
  // Bottom tier (4)
  { label: "Collect", icon: Banknote, col: 20, tier: 2 },
  { label: "Claim", icon: ShieldCheck, col: 40, tier: 2 },
  { label: "Resolve", icon: CheckCircle2, col: 60, tier: 2 },
  { label: "Refund", icon: Undo2, col: 80, tier: 2 },
];

/* Orthogonal polyline -> smooth path. Every interior corner is replaced by a quarter-circle arc
   of the given radius (per corner, or one radius for all); a corner whose two arcs meet with no
   straight run between them becomes a clean S-bend. Returns the path data plus its exact length,
   which sets the comet's speed. */
function route(points, radius) {
  const r = (i) => (Array.isArray(radius) ? radius[i] : radius);
  const dist = (a, b) => Math.hypot(b[0] - a[0], b[1] - a[1]);
  const dir = (a, b) => {
    const L = dist(a, b);
    return [(b[0] - a[0]) / L, (b[1] - a[1]) / L];
  };
  const f = (n) => Number(n.toFixed(2));
  let d = `M ${f(points[0][0])} ${f(points[0][1])}`;
  let length = 0;
  let cur = points[0];
  for (let i = 1; i < points.length - 1; i++) {
    const prev = points[i - 1];
    const p = points[i];
    const next = points[i + 1];
    const rr = r(i - 1);
    const u = dir(prev, p);
    const v = dir(p, next);
    const a = [p[0] - u[0] * rr, p[1] - u[1] * rr];
    const b = [p[0] + v[0] * rr, p[1] + v[1] * rr];
    if (dist(cur, a) > 0.01) {
      length += dist(cur, a);
      d += ` L ${f(a[0])} ${f(a[1])}`;
    }
    // In SVG's y-down space a positive cross product is a clockwise turn on screen.
    const sweep = u[0] * v[1] - u[1] * v[0] > 0 ? 1 : 0;
    d += ` A ${rr} ${rr} 0 0 ${sweep} ${f(b[0])} ${f(b[1])}`;
    length += (Math.PI * rr) / 2;
    cur = b;
  }
  const end = points[points.length - 1];
  length += dist(cur, end);
  d += ` L ${f(end[0])} ${f(end[1])}`;
  return { d, length };
}

/* Outer strand: trunk -> shoulder onto the bar -> along the bar -> corner down -> column. */
function barStrand(col, tier) {
  const x1 = COL(col);
  return route(
    [
      [CENTER, 0],
      [CENTER, BAR_Y],
      [x1, BAR_Y],
      [x1, TOP(tier)],
    ],
    [FORK_R, DROP_R],
  );
}

/* Claim / Resolve: trunk -> second fork -> corridor -> down into the column under the tier-0 tile. */
function corridorStrand(col, tier) {
  const x1 = COL(col);
  return route(
    [
      [CENTER, 0],
      [CENTER, CORRIDOR_Y],
      [x1, CORRIDOR_Y],
      [x1, TOP(tier)],
    ],
    JOG_R,
  );
}

const STRANDS = {
  Collect: barStrand(20, 2),
  Market: barStrand(30, 1),
  Track: barStrand(40, 0),
  Claim: corridorStrand(40, 2),
  Analyze: route([[CENTER, 0], [CENTER, TOP(1)]], 0),
  Resolve: corridorStrand(60, 2),
  Manage: barStrand(60, 0),
  Sync: barStrand(70, 1),
  Refund: barStrand(80, 2),
};

/* Timing. One cycle is RS_TREE_PERIOD seconds; the comets travel during the first
   TRAVEL_FRACTION of it (that is when --rs-t runs 0 -> 1). TIER_ARRIVAL is when each tier's
   comets reach their tiles, in seconds from launch; each comet's speed follows from its own
   strand length. The tile glow starts LIGHT_LAG after the head lands. */
export const RS_TREE_PERIOD = 6;
const TRAVEL_FRACTION = 0.75;
const TIER_ARRIVAL = [1.1, 2.2, 3.2];
const LIGHT_LAG = 0.08;
const COMET = 28; // comet length in geometry units
const travelWindow = RS_TREE_PERIOD * TRAVEL_FRACTION;
const tau = (tier) => TIER_ARRIVAL[tier] / travelWindow; // head-arrival time as a fraction of --rs-t

/* The tree is laid out once at its design width (34rem) and scaled down as a whole, with CSS
   zoom, wherever the column is narrower (tablet card columns, phones). Scaling uniformly keeps
   the arcs round, the strand spacing legible and the tiles clear of each other. */
const DESIGN_WIDTH_REM = 34;

function useFitScale() {
  const ref = useRef(null);
  const [scale, setScale] = useState(1);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const rem = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
    const fit = () => setScale(Math.min(1, el.clientWidth / (DESIGN_WIDTH_REM * rem)));
    fit();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, scale];
}

export function RentSuccessBackOfficeTree() {
  const [fitRef, scale] = useFitScale();
  return (<div ref={fitRef} className="rs-tree-fit w-full">
    <div className="rs-tree relative mx-auto mt-6 w-[34rem] max-w-none" style={{ "--rs-period": `${RS_TREE_PERIOD}s`, "--rs-travel": `${Math.round(TRAVEL_FRACTION * 100)}%`, "--rs-comet": COMET, zoom: scale }}>
      {/* Main mark: rentSuccess brand mark on a black-glass circle, centered over the trunk. The
          glow pulses in step with each launch. */}
      <div className="relative flex justify-center">
        <span className="rs-tree-logo relative flex h-[5.5rem] w-[5.5rem] items-center justify-center rounded-full bg-black/60 ring-1 ring-white/15">
          <span aria-hidden="true" className="rs-tree-logo-glow pointer-events-none absolute -inset-3 rounded-full"/>
          <Image src="/images/rentid-suite/rentsuccess-mark.png" alt="rentSuccess" width={96} height={96} className="relative h-16 w-16 object-contain"/>
        </span>
      </div>

      {/* Diagram canvas: SVG strands sit behind absolutely-positioned tiles. */}
      <div className="relative mt-1 h-[18rem]">
        <svg aria-hidden="true" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="pointer-events-none absolute inset-0 h-full w-full">
          {/* Resting track. Each strand is a full path from the mark, so the trunk is drawn nine
              times; the opacity sits on the group (not the stroke) so the shared segments do not
              stack up brighter than the branches. Non-scaling strokes keep the hairlines crisp. */}
          <g stroke="#ffffff" opacity={0.2} strokeWidth={1} strokeLinecap="round" fill="none">
            {TILES.map((tile) => (<path key={tile.label} d={STRANDS[tile.label].d} vectorEffect="non-scaling-stroke"/>))}
          </g>

          {/* Nine comets, launched together. pathLength pins the dash units to the geometry units;
              --rs-len and --rs-tau give home.css what it needs to place this comet's head at
              (--rs-t / --rs-tau) of the way along, so it lands exactly at its tier's moment. */}
          {/* No non-scaling-stroke here on purpose: with it, Chrome measures the dash pattern in
              screen pixels, so the comets fell short of their tiles by the canvas scale. Drawing
              them in user units keeps dash, offset and pathLength in one unit space; the canvas
              is scaled uniformly (1.6px per unit at design size, then zoom), so the stroke stays
              round. */}
          <g stroke="#c4b0ff" strokeWidth={1} strokeLinecap="round" fill="none">
            {TILES.map((tile) => {
            const s = STRANDS[tile.label];
            return (<path key={tile.label} className="rs-tree-beam" d={s.d} pathLength={s.length} strokeDasharray={`${COMET} ${W * H}`} style={{ "--rs-len": s.length.toFixed(2), "--rs-tau": tau(tile.tier).toFixed(4) }}/>);
        })}
          </g>
        </svg>

        {/* Nine glass tiles: icon on top, label beneath. A tile's glow is delayed to the moment
            its comet lands, so every tile in a tier lights together. No backdrop-filter on the
            tiles or the mark: Chrome intermittently dropped backdrop-filtered layers inside this
            zoomed subtree while scrolling (tiles vanished until the next repaint), and over a flat
            gradient the blur was invisible anyway. */}
        {TILES.map((tile) => (<div key={tile.label} className="rs-tile absolute z-20 flex h-[3.25rem] w-[3.25rem] -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center gap-1 rounded-xl bg-white/[0.12] text-white ring-1 ring-white/25" style={{ left: `${(COL(tile.col) / W) * 100}%`, top: `${(TIER_Y[tile.tier] / H) * 100}%`, animationDelay: `${(TIER_ARRIVAL[tile.tier] + LIGHT_LAG).toFixed(2)}s` }}>
            <tile.icon aria-hidden="true" className="rs-tile-icon h-4 w-4 shrink-0"/>
            <span className="text-center font-sans text-[0.55rem] font-semibold leading-none text-white/90">
              {tile.label}
            </span>
          </div>))}
      </div>
    </div>
  </div>);
}
